import * as React from 'react';
import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  CSSProperties,
  KeyboardEventHandler,
  MouseEventHandler,
  ReactNode,
} from 'react';
import { cx } from '../utils';
import type { Density } from '../density';

/* Outer wrapper for TableHeader + TableRow children. Applies the
   role="table" ancestor that ARIA requires around columnheader /
   row. Before this primitive existed, consumers rendering
   TableHeader + TableRow directly inside a plain <div> produced
   an incomplete ARIA tree — axe-core in strict modes would flag
   role="row" as missing a required parent.

   Use <Table> whenever you compose the primitives. Skip it only
   if you're nesting inside a native <table> (the native role
   subsumes the wrapper). */
export interface TableProps {
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  /* Accessibility label for the whole table. Pass a short,
     descriptive phrase ("Team members", "Recent transactions");
     renders as aria-label on the role="table" root. Omit when
     the surrounding context already labels the table via a
     heading + aria-labelledby. */
  ariaLabel?: string;
  /* Per-component density (ADR-008). Sets data-density on the
     root element so density.css overrides kick in.
     - omitted: inherits the ambient density from <JacquardProvider>
       via density.css's `:where(:root)[data-density]` selectors.
     - 'default' (explicit): opts THIS table out of any ambient
       density — `:not([data-density='default'])` excludes it.
     - 'compact' | 'comfortable': per-component override.
     Codex audit #108 R7. */
  density?: Density;
  /* Divider policy for direct TableRow children.
     - internal: dividers only between rows (default)
     - all: every row carries its bottom divider
     - none: no row dividers */
  rowDividers?: 'internal' | 'all' | 'none';
}

export function Table({
  children,
  className,
  style,
  ariaLabel,
  density,
  rowDividers = 'internal',
}: TableProps) {
  return (
    <div
      className={cx('j-table', `j-table-row-dividers-${rowDividers}`, className)}
      role="table"
      aria-label={ariaLabel}
      style={style}
      data-density={density}
    >
      {children}
    </div>
  );
}

export interface TableColumn {
  label?: ReactNode;
  w?: string;
  align?: 'left' | 'right' | 'center';
  /* If set, the header cell becomes a <button> with click +
     Enter/Space keyboard activation. Previously the handler was
     attached to a plain <div>, which was pointer-only and invisible
     to assistive tech. */
  onSort?: () => void;
  sort?: 'asc' | 'desc' | null;
  /* Stable key for reconciliation when the column list is
     state-driven. Falls back to label → index if omitted, matching
     the 3.0.0 behaviour for static columns. */
  key?: string | number;
  /* Accessible name for the sort button when `label` is empty or a
     non-text ReactNode (e.g. an icon-only header). Without this,
     the <button type="button"> inside a sortable columnheader has
     no accessible name and is announced as "button". If unset and
     label is a string/number, we fall back to the label text
     automatically; icon headers should pass sortLabel explicitly. */
  sortLabel?: string;
}

/* Resolve a human-readable accessible name for a sortable column's
   <button>. Priority: explicit sortLabel > stringifiable label >
   a generic "Sort column" fallback that always produces some name
   so screen readers never announce a bare "button". */
function sortButtonName(c: TableColumn): string {
  if (c.sortLabel) return c.sortLabel;
  if (typeof c.label === 'string' || typeof c.label === 'number') return String(c.label);
  return 'Sort column';
}

function colKey(c: TableColumn, i: number): string | number {
  if (c.key != null) return c.key;
  if (typeof c.label === 'string' || typeof c.label === 'number') return c.label;
  return i;
}

export interface TableHeaderProps {
  cols: TableColumn[];
  className?: string;
  style?: CSSProperties;
}

export function TableHeader({ cols, className, style }: TableHeaderProps) {
  const gridTemplate = cols.map((c) => c.w || 'minmax(0, 1fr)').join(' ');
  return (
    <div
      className={cx('j-table-header', className)}
      role="row"
      style={{ gridTemplateColumns: gridTemplate, ...style }}
    >
      {cols.map((c, i) => {
        const align = c.align || 'left';
        const label = (
          <>
            {c.label}
            {c.sort && <span className="j-table-cell-sort">{c.sort === 'asc' ? '↑' : '↓'}</span>}
          </>
        );
        /* aria-sort only has meaning on a columnheader / rowheader;
           on a plain <button> it's ignored. Put it on the cell
           wrapper (with role="columnheader") and keep the <button>
           for activation only. Visible sort arrow stays in the
           label; assistive tech gets the announced state. */
        const ariaSort: 'ascending' | 'descending' | 'none' | undefined = c.onSort
          ? c.sort === 'asc'
            ? 'ascending'
            : c.sort === 'desc'
              ? 'descending'
              : 'none'
          : undefined;
        if (c.onSort) {
          return (
            <div
              key={colKey(c, i)}
              role="columnheader"
              aria-sort={ariaSort}
              className="j-table-cell-label j-table-cell-sortable"
              style={{ textAlign: align }}
            >
              <button
                type="button"
                className="j-table-cell-sort-btn"
                onClick={c.onSort}
                aria-label={sortButtonName(c)}
                style={{ textAlign: align }}
              >
                {label}
              </button>
            </div>
          );
        }
        return (
          <div
            key={colKey(c, i)}
            role="columnheader"
            className="j-table-cell-label"
            style={{ textAlign: align, cursor: 'default' }}
          >
            {label}
          </div>
        );
      })}
    </div>
  );
}

/* Activation event for a clickable TableRow. Narrowed to the union
   of the two real event kinds the handler actually receives — a
   click on the row or a keyboard activation (Enter / Space). Do
   NOT cast a keyboard event to MouseEvent: consumers reading
   e.button / e.clientX / e.altKey get `undefined`-ish values when
   the activation was a keypress. */
export type TableRowActivationEvent =
  | React.MouseEvent<HTMLDivElement>
  | React.KeyboardEvent<HTMLDivElement>;
export type TableRowActivationHandler = (e: TableRowActivationEvent) => void;

export interface TableRowProps {
  cols: TableColumn[];
  /* When set, the row becomes an activation target (role="button",
     tabIndex=0, Enter/Space → onClick). Accepts either the real
     mouse event (pointer activation) or the real keyboard event
     (keyboard activation). */
  onClick?: TableRowActivationHandler;
  /* Keyboard handler; caller can short-circuit row activation by
     calling e.preventDefault(). */
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  dim?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

export function TableRow({
  cols,
  onClick,
  onKeyDown,
  dim,
  className,
  style,
  children,
}: TableRowProps) {
  const gridTemplate = cols.map((c) => c.w || 'minmax(0, 1fr)').join(' ');
  const isClickable = Boolean(onClick);

  /* Walk up from the event target and skip row activation only
     when the origin lives inside a nested INTERACTIVE descendant
     (button, link, form control, role=button, explicit tabindex).
     The earlier `e.target === e.currentTarget` check was too
     strict — a normal row with plain <div>Ada</div> cells never
     activated because e.target is the cell div, not the row.
     Plain cell content still fires activation; only real
     double-fire cases (a Button in the row) are suppressed. */
  const INTERACTIVE_DESCENDANT =
    'button, a[href], input, select, textarea, [role="button"], [role="link"], [role="menuitem"], [role="checkbox"], [tabindex]:not([tabindex="-1"])';

  const isOriginatedFromNestedInteractive = (e: React.SyntheticEvent<HTMLDivElement>): boolean => {
    const target = e.target as Element | null;
    if (!target || !('closest' in target)) return false;
    /* `closest` walks target and its ancestors until it finds a
       match OR hits the document root. Scope the query to the
       row element by checking the ancestry contains a match
       inside the row but NOT the row itself. */
    const interactive = target.closest(INTERACTIVE_DESCENDANT);
    if (!interactive) return false;
    /* The row is the currentTarget; interactive must be strictly
       inside it (not the row itself). */
    if (interactive === e.currentTarget) return false;
    return e.currentTarget.contains(interactive);
  };

  const handleClick: MouseEventHandler<HTMLDivElement> | undefined = isClickable
    ? (e) => {
        if (isOriginatedFromNestedInteractive(e)) return;
        onClick?.(e);
      }
    : undefined;

  const handleKey: KeyboardEventHandler<HTMLDivElement> | undefined = isClickable
    ? (e) => {
        if (onKeyDown) onKeyDown(e);
        if (e.defaultPrevented) return;
        if (isOriginatedFromNestedInteractive(e)) return;
        /* `e.key === ' '` covers modern browsers. Old IE / pre-78
           Firefox emitted 'Spacebar'; we don't support those, but
           if the browser matrix ever widens, add 'Spacebar' to
           the alternation here. */
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          /* Pass the real KeyboardEvent, not a synthesized
             MouseEvent cast. The onClick type includes both event
             kinds, so this is type-safe and honest. */
          onClick?.(e);
        }
      }
    : onKeyDown;

  /* role="row" is the ARIA-correct ancestor for the role="cell" /
     role="columnheader" children inside. When the row is clickable
     we use role="button" instead, because ARIA doesn't allow a
     row to double as an interactive activation target.
     ⚠ KNOWN TRADEOFF: when role="button" is on the row and the
     caller puts <div role="cell"> children inside, the cells are
     technically parented by a button, which is invalid ARIA
     (cells only nest under row/group). axe-core doesn't surface
     this because the cells are plain divs, but assistive tech
     can still announce inconsistently. Documented + called out
     in the 4.0.0 changeset. For a strict ARIA grid under a
     clickable row, use an inert row (omit onClick) with a
     button-shaped cell instead. A future TableRowAction or
     TableRowLink primitive can ship the idiomatic alternative;
     tracked in docs/DEFERRED.md. */
  return (
    <div
      className={cx('j-table-row', className)}
      data-clickable={isClickable ? 'true' : undefined}
      data-dim={dim ? 'true' : undefined}
      onClick={handleClick}
      onKeyDown={handleKey}
      role={isClickable ? 'button' : 'row'}
      tabIndex={isClickable ? 0 : undefined}
      style={{ gridTemplateColumns: gridTemplate, ...style }}
    >
      {children}
    </div>
  );
}

interface TableRowActionBaseProps {
  cols: TableColumn[];
  children?: ReactNode;
  dim?: boolean;
  className?: string;
  style?: CSSProperties;
  actionLabel: ReactNode;
  actionColumnWidth?: string;
}

export interface TableRowActionProps
  extends
    TableRowActionBaseProps,
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'className' | 'style'> {}

export interface TableRowLinkProps
  extends
    TableRowActionBaseProps,
    Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'children' | 'className' | 'style'> {
  href: string;
}

function actionCols(cols: TableColumn[], actionColumnWidth?: string): TableColumn[] {
  return [
    ...cols,
    {
      key: '__j-table-row-action',
      label: '',
      w: actionColumnWidth ?? 'max-content',
    },
  ];
}

/* Strict-ARIA row activation alternative. Unlike <TableRow onClick>,
   the row remains role="row"; activation lives in a real button inside
   an action cell, so role="cell" descendants keep a valid table tree. */
export const TableRowAction = React.forwardRef<HTMLButtonElement, TableRowActionProps>(
  function TableRowAction(
    {
      cols,
      children,
      dim,
      className,
      style,
      actionLabel,
      actionColumnWidth,
      type = 'button',
      ...buttonProps
    },
    ref,
  ) {
    return (
      <TableRow
        cols={actionCols(cols, actionColumnWidth)}
        dim={dim}
        className={className}
        style={style}
      >
        {children}
        <div role="cell" className="j-table-row-action-cell">
          <button ref={ref} type={type} className="j-table-row-action" {...buttonProps}>
            {actionLabel}
          </button>
        </div>
      </TableRow>
    );
  },
);

export const TableRowLink = React.forwardRef<HTMLAnchorElement, TableRowLinkProps>(
  function TableRowLink(
    { cols, children, dim, className, style, actionLabel, actionColumnWidth, ...anchorProps },
    ref,
  ) {
    return (
      <TableRow
        cols={actionCols(cols, actionColumnWidth)}
        dim={dim}
        className={className}
        style={style}
      >
        {children}
        <div role="cell" className="j-table-row-action-cell">
          <a ref={ref} className="j-table-row-action" {...anchorProps}>
            {actionLabel}
          </a>
        </div>
      </TableRow>
    );
  },
);
